import { useState } from 'react';
import { InformationCircleIcon, ClockIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { Dialog } from '@/components/ui/Dialog';
import { KnowledgeLink } from '@/components/ui/KnowledgeLink';

interface ConfirmationInfoButtonProps {
    entityLabel: string; // "value" or "balance"
}

/**
 * A button with an info icon that opens a dialog explaining the confirmation date concept.
 * Used on the ledger values tab to explain what "Latest confirmation" means.
 */
export function ConfirmationInfoButton({ entityLabel }: ConfirmationInfoButtonProps) {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="p-0.5 text-gray-400 hover:text-accent transition-colors"
                title="About this date"
            >
                <InformationCircleIcon className="h-4 w-4" />
            </button>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                title="About Confirmation Date"
                size="sm"
            >
                <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                            What is shown here?
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                            This shows when you last confirmed this ledger is up-to-date. If you
                            haven't confirmed yet, it shows the date of your latest recorded{' '}
                            {entityLabel}.
                        </p>
                    </div>

                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                            How to use
                        </p>
                        <ul className="list-disc list-inside space-y-1 text-gray-500 dark:text-gray-400">
                            <li>
                                <ClockIcon className="inline h-4 w-4 text-yellow-500" /> - Mark
                                as up-to-date (sets confirmed date to your most recent activity across all ledgers)
                            </li>
                            <li>
                                <CheckCircleIcon className="inline h-4 w-4 text-accent" /> - Reset
                                confirmation (reverts to latest {entityLabel} date)
                            </li>
                        </ul>
                    </div>

                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">
                            Why does this matter?
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                            The confirmation date acts as a boundary for all analytics. Charts,
                            P&L calculations, and performance metrics only include data up to this
                            date. This ensures consistent reporting even when you haven't recorded
                            recent values.
                        </p>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Example</p>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">
                            If today is January 15th and your confirmation date is January 10th,
                            analytics will use exchange rates from January 10th and exclude any
                            transactions after that date.
                        </p>
                    </div>

                    <p className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <KnowledgeLink page="confirmation">Learn more about Confirmation</KnowledgeLink>
                    </p>
                </div>
            </Dialog>
        </>
    );
}
